/**
 * 工作台首页
 *
 * 按角色展示不同内容：待办、风险提示、快捷入口。
 * 首页同时承担「业务口径告知」的职责 —— 把平台的三条硬约束写清楚，
 * 避免使用者误以为系统会自动生成凭证或跳过人工审核。
 */

import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Card,
  Col,
  Descriptions,
  List,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowRightOutlined,
  AuditOutlined,
  CloudUploadOutlined,
  FileSearchOutlined,
  ReconciliationOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  DATA_SCOPE_LABELS,
  DefaultRoleCode,
  InvoiceStatus,
  INVOICE_STATUS_LABELS,
  PROVIDER_LABELS,
  PermissionCode,
  RiskLevel,
  RISK_LEVEL_LABELS,
  formatCents,
  type FlowTask,
} from '@ilc/shared';
import { useAuthStore } from '@/stores/auth';
import { useResource } from '@/hooks/useResource';
import { flowApi, metaApi } from '@/api/endpoints';
import { mockFlowTaskList, mockTenantMetricData } from '@/mocks';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { RiskTag, RISK_COLORS } from '@/components/RiskTag';

/** 三条业务硬约束 —— 直接来自需求文档，写在首页最显眼的位置 */
function BusinessConstraints(): React.ReactElement {
  return (
    <Alert
      type="info"
      showIcon
      icon={<SafetyCertificateOutlined />}
      message="平台业务边界（重要）"
      description={
        <ul style={{ margin: '4px 0 0', paddingInlineStart: 18, lineHeight: 1.9 }}>
          <li>
            <strong>不自动生成凭证</strong>：本平台仅把发票业务数据同步到 T+「待办业务池」，凭证必须由财务人员在
            T+ 端人工选择会计科目后生成。
          </li>
          <li>
            <strong>自动化只做风险标记</strong>：OCR、企查查、税务局、合同比对的结果只作为参考，流程中保留
            财务初审人工确认节点，不由系统直接放行。
          </li>
          <li>
            <strong>红冲 / 作废默认阻断</strong>：高风险发票默认禁止流转 T+，确需入账须走「特例放行」并填写
            专项审批依据，全程留痕。
          </li>
          <li>
            <strong>外部校验不可用时可人工接管</strong>：接口异常时标记「外部校验暂不可用」，人工核验并填写
            说明后业务继续，不卡死流程。
          </li>
        </ul>
      }
    />
  );
}

interface QuickEntry {
  title: string;
  desc: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
}

const QUICK_ENTRIES: QuickEntry[] = [
  {
    title: '上传发票',
    desc: '批量或拖拽上传，按票种提示查验要素',
    path: '/business/upload',
    icon: <CloudUploadOutlined />,
    permission: PermissionCode.INVOICE_CREATE,
  },
  {
    title: '我的发票',
    desc: '查看上传记录、关联合同、处理退回',
    path: '/business/invoices',
    icon: <FileSearchOutlined />,
    permission: PermissionCode.INVOICE_READ,
  },
  {
    title: '财务初审',
    desc: '审阅校验报告、人工复核、特例放行',
    path: '/business/review',
    icon: <AuditOutlined />,
    permission: PermissionCode.INVOICE_REVIEW,
  },
  {
    title: '记账同步',
    desc: '查看已推送 T+ 的发票与凭证回写状态',
    path: '/business/bookkeeping',
    icon: <ReconciliationOutlined />,
    permission: PermissionCode.TPLUS_READ,
  },
  {
    title: '合同与抽查',
    desc: '合同管理、归档检索、事后抽查',
    path: '/business/legal',
    icon: <SafetyCertificateOutlined />,
    permission: PermissionCode.CONTRACT_READ,
  },
  {
    title: '档案库',
    desc: '按供应商、合同、凭证号检索追溯',
    path: '/business/archive',
    icon: <FileSearchOutlined />,
    permission: PermissionCode.ARCHIVE_READ,
  },
];

export default function Dashboard(): React.ReactElement {
  const auth = useAuthStore((s) => s.auth);
  const can = useAuthStore((s) => s.can);
  const navigate = useNavigate();

  const metrics = useResource(() => metaApi.tenantMetrics(), { fallback: () => mockTenantMetricData() });
  const tasks = useResource(() => flowApi.tasks({ page: 1, pageSize: 8, mine: true }), {
    fallback: () => mockFlowTaskList({ page: 1, pageSize: 8, mine: true }),
  });

  const roles = auth?.roles.map((r) => r.name).join('、') || '未分配角色';
  const quickEntries = QUICK_ENTRIES.filter((e) => !e.permission || can(e.permission));

  const statusData = metrics.data
    ? Object.entries(metrics.data.invoiceByStatus).map(([status, count]) => ({
        status: status as InvoiceStatus,
        label: INVOICE_STATUS_LABELS[status as InvoiceStatus] ?? status,
        count,
      }))
    : [];

  const riskData = metrics.data
    ? (Object.entries(metrics.data.riskDistribution) as Array<[RiskLevel, number]>).map(([level, count]) => ({
        level,
        label: RISK_LEVEL_LABELS[level],
        count,
      }))
    : [];

  return (
    <div className="ilc-page">
      <PageHeader
        title={`您好，${auth?.name ?? ''}`}
        description={`${auth?.tenantName ?? '租户'} · ${roles}${
          auth ? ` · 数据权限：${DATA_SCOPE_LABELS[auth.dataScope]}` : ''
        }`}
        tags={
          <>
            {auth?.maxRiskLevel ? (
              <Tooltip title="可放行的最高风险等级，超过需更高权限人员处理">
                <Tag color="orange">可放行至 {RISK_LEVEL_LABELS[auth.maxRiskLevel]}</Tag>
              </Tooltip>
            ) : null}
            {auth?.amountLimitCents !== null && auth?.amountLimitCents !== undefined ? (
              <Tag color="blue">审核金额上限 ¥{formatCents(auth.amountLimitCents)}</Tag>
            ) : (
              <Tag>审核金额不限</Tag>
            )}
          </>
        }
      />

      <BusinessConstraints />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic title="待我处理" value={metrics.data?.myPendingTasks ?? 0} suffix="项" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="待财务初审"
              value={
                metrics.data?.invoiceByStatus[InvoiceStatus.PENDING_REVIEW] ?? 0
              }
              suffix="张"
              valueStyle={{ color: '#d46b08' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="高风险发票"
              value={metrics.data?.riskDistribution[RiskLevel.HIGH] ?? 0}
              suffix="张"
              valueStyle={{ color: RISK_COLORS[RiskLevel.HIGH] }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="已推送 T+ 待记账"
              value={
                (metrics.data?.invoiceByStatus[InvoiceStatus.PUSHED] ?? 0) +
                (metrics.data?.invoiceByStatus[InvoiceStatus.BOOKED] ?? 0)
              }
              suffix="张"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="我的待办" size="small" extra={<Link to="/business/review">全部待办 <ArrowRightOutlined /></Link>}>
            <PageState
              loading={tasks.loading}
              error={tasks.error}
              onRetry={tasks.reload}
              empty={(tasks.data?.list.length ?? 0) === 0}
              emptyText="当前没有待您处理的发票，可先上传发票或查看档案库"
              skeleton
              rows={4}
            >
              <Table<FlowTask>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={tasks.data?.list ?? []}
                columns={[
                  { title: '节点', dataIndex: 'nodeName', width: 140 },
                  {
                    title: '处理角色',
                    dataIndex: 'assigneeRoleCode',
                    width: 130,
                    render: (v: string | null) =>
                      v ? (
                        <Tag color="blue">{v}</Tag>
                      ) : (
                        <Tag>系统</Tag>
                      ),
                  },
                  {
                    title: '超时',
                    dataIndex: 'dueAt',
                    width: 100,
                    render: (v: string | null) =>
                      v && new Date(v) < new Date() ? <Tag color="red">已超时</Tag> : <Tag color="green">正常</Tag>,
                  },
                  {
                    title: '',
                    key: 'action',
                    render: (_v, record) => (
                      <Link to={`/business/invoices/${record.instanceId.replace('fi-', '')}`}>去处理</Link>
                    ),
                  },
                ]}
              />
            </PageState>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="发票状态分布" size="small">
            <PageState loading={metrics.loading} error={metrics.error} onRetry={metrics.reload} skeleton rows={4}>
              <List
                size="small"
                dataSource={statusData.filter((s) => s.count > 0)}
                renderItem={(item) => (
                  <List.Item>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <span>{item.label}</span>
                      <Typography.Text strong className="ilc-num">
                        {item.count} 张
                      </Typography.Text>
                    </Space>
                  </List.Item>
                )}
                locale={{ emptyText: '暂无发票数据' }}
              />
            </PageState>
          </Card>

          <Card title="风险分布" size="small" style={{ marginTop: 16 }}>
            <PageState loading={metrics.loading} error={metrics.error} onRetry={metrics.reload} skeleton rows={4}>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {riskData.map((item) => (
                  <div key={item.level}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <RiskTag level={item.level} withHint={false} />
                      <Typography.Text strong className="ilc-num">
                        {item.count} 张
                      </Typography.Text>
                    </Space>
                    <Progress
                      percent={
                        riskData.reduce((sum, r) => sum + r.count, 0) === 0
                          ? 0
                          : Math.round((item.count / riskData.reduce((sum, r) => sum + r.count, 0)) * 100)
                      }
                      strokeColor={RISK_COLORS[item.level]}
                      size="small"
                      showInfo={false}
                    />
                  </div>
                ))}
              </Space>
            </PageState>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="快捷入口" size="small">
            <Row gutter={[12, 12]}>
              {quickEntries.map((entry) => (
                <Col xs={24} sm={12} key={entry.path}>
                  <Card
                    size="small"
                    hoverable
                    onClick={() => navigate(entry.path)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Space>
                      <span style={{ fontSize: 18, color: '#1677ff' }}>{entry.icon}</span>
                      <div>
                        <div style={{ fontWeight: 600 }}>{entry.title}</div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {entry.desc}
                        </Typography.Text>
                      </div>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="本月接口用量" size="small">
            <PageState loading={metrics.loading} error={metrics.error} onRetry={metrics.reload} skeleton rows={4}>
              <Space direction="vertical" style={{ width: '100%' }} size={10}>
                {(metrics.data?.quotaUsage ?? []).map((quota) => {
                  const percent = quota.limit === 0 ? 0 : Math.min(100, Math.round((quota.used / quota.limit) * 100));
                  return (
                    <div key={quota.provider}>
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <span>{PROVIDER_LABELS[quota.provider] ?? quota.provider}</span>
                        <Typography.Text className="ilc-num" type={percent >= 90 ? 'danger' : undefined}>
                          {quota.used} / {quota.limit}
                        </Typography.Text>
                      </Space>
                      <Progress
                        percent={percent}
                        size="small"
                        status={percent >= 100 ? 'exception' : percent >= 90 ? 'active' : 'normal'}
                      />
                    </div>
                  );
                })}
              </Space>
            </PageState>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              配额与超额告警阈值由租户管理员在「外部接口配置」中调整
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Card size="small" title="我的权限上下文" style={{ marginTop: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
          <Descriptions.Item label="账号">{auth?.username}</Descriptions.Item>
          <Descriptions.Item label="主体类型">
            {auth?.principalType === 'PLATFORM' ? '平台运营人员' : '租户用户'}
          </Descriptions.Item>
          <Descriptions.Item label="所属部门">{auth?.deptId ? '已分配' : '未分配'}</Descriptions.Item>
          <Descriptions.Item label="角色">{roles}</Descriptions.Item>
          <Descriptions.Item label="数据权限">{auth ? DATA_SCOPE_LABELS[auth.dataScope] : '-'}</Descriptions.Item>
          <Descriptions.Item label="功能权限数">{auth?.permissions.length ?? 0} 项</Descriptions.Item>
        </Descriptions>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          默认角色说明：{DefaultRoleCode.PROJECT_HANDLER} 上传与关联合同、{DefaultRoleCode.FINANCE_REVIEWER}{' '}
          审阅与放行、{DefaultRoleCode.FINANCE_BOOKKEEPER} 在 T+ 端记账、{DefaultRoleCode.LEGAL} 合同与抽查、
          {DefaultRoleCode.TECH_OPS} 接口与日志。
        </Typography.Text>
      </Card>
    </div>
  );
}
